import { useEffect, useState } from 'react';

// ---- Scroll progress (0..1 over the whole scrollable document) ----

type Listener = (p: number) => void;
const listeners = new Set<Listener>();
let currentProgress = 0;
let attached = false;

// The sticky hero occupies the FIRST 500vh of the document. It progresses
// from 0 → 1 as the user scrolls through those first 400vh (500vh height
// minus one viewport of sticky overlap). Anchoring to the hero's own
// window — not `document.scrollHeight` — keeps the hero timeline stable
// no matter how much content is appended below it.
const HERO_HEIGHT_VH = 500;

function computeProgress() {
  const vh = window.innerHeight;
  const heroScrollRange = Math.max(1, (HERO_HEIGHT_VH / 100 - 1) * vh);
  const p = window.scrollY / heroScrollRange;
  return Math.max(0, Math.min(1, p));
}

function ensureAttached() {
  if (attached || typeof window === 'undefined') return;
  attached = true;
  const update = () => {
    currentProgress = computeProgress();
    listeners.forEach((cb) => cb(currentProgress));
  };
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
}

export function useScrollProgress() {
  const [p, setP] = useState(currentProgress);
  useEffect(() => {
    ensureAttached();
    setP(currentProgress);
    listeners.add(setP);
    return () => {
      listeners.delete(setP);
    };
  }, []);
  return p;
}

export function subscribeScrollProgress(cb: Listener) {
  ensureAttached();
  listeners.add(cb);
  cb(currentProgress);
  return () => {
    listeners.delete(cb);
  };
}

export function getScrollProgress() {
  return currentProgress;
}

// ---- Anchor label positions (updated per-frame from ProductShowcase) ----

export type AnchorLabel = {
  id: string;
  label: string;
  value: string;
  x: number;
  y: number;
  side: 'left' | 'right';
  onScreen: boolean;
};

export const heroSignals = {
  anchors: [] as AnchorLabel[],
  anchorsVersion: 0,
};

export function pushAnchors(anchors: AnchorLabel[]) {
  // Skip if both current and incoming are empty to avoid needless re-renders.
  if (anchors.length === 0 && heroSignals.anchors.length === 0) return;
  heroSignals.anchors = anchors;
  heroSignals.anchorsVersion += 1;
}

// ---- Helpers ----

export function smoothstep01(x: number) {
  const t = Math.max(0, Math.min(1, x));
  return t * t * (3 - 2 * t);
}

export function mapRange(x: number, a: number, b: number) {
  if (b === a) return 0;
  return smoothstep01((x - a) / (b - a));
}